import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Receipt, Pencil, CheckCircle, AlertTriangle, Loader2, FileUp, Plus } from 'lucide-react';
import { useCreditCardSummary } from '@/hooks/use-credit-card-summary';

interface Account {
  id: string;
  name: string;
  type: string;
  institution: string | null;
  balance: number;
  credit_limit: number | null;
  closing_day: number | null;
  due_day: number | null;
  color: string;
}

interface CreditCardAccountCardProps {
  account: Account;
  onEdit: () => void;
  onViewInvoice: () => void;
  onImport: () => void;
  onAddExpense: () => void;
  formatCurrency: (value: number) => string;
  refreshKey?: number;
}

export function CreditCardAccountCard({
  account,
  onEdit,
  onViewInvoice,
  onImport,
  onAddExpense,
  formatCurrency,
  refreshKey
}: CreditCardAccountCardProps) {
  const { currentInvoiceTotal, isPaid, loading, isOverdue } = useCreditCardSummary(
    account.id,
    account.closing_day,
    account.due_day,
    refreshKey
  );

  return (
    <Card className="overflow-hidden">
      <div
        className="h-2"
        style={{ backgroundColor: account.color }}
      />
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>{account.name}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-normal">
              Cartão
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm text-muted-foreground">Carregando...</span>
              </div>
            ) : (
              <>
                <div className={`text-2xl font-bold ${currentInvoiceTotal > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {formatCurrency(Math.max(0, currentInvoiceTotal))}
                </div>
                <p className="text-xs text-muted-foreground">Fatura atual</p>
              </>
            )}
          </div>
          {!loading && (
            <div>
              {isPaid && (
                <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
                  <CheckCircle className="h-3 w-3" />
                  Paga
                </Badge>
              )}
              {isOverdue && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Vencida
                </Badge>
              )}
            </div>
          )}
        </div>
        
        {account.credit_limit && (
          <p className="text-sm text-muted-foreground mt-1">
            Limite: {formatCurrency(Number(account.credit_limit))}
          </p>
        )}
        {account.closing_day && account.due_day && (
          <p className="text-xs text-muted-foreground">
            Fecha dia {account.closing_day} • Vence dia {account.due_day}
          </p>
        )}
        {account.institution && (
          <p className="text-sm text-muted-foreground mt-1">
            {account.institution}
          </p>
        )}
        
        <div className="flex gap-1.5 mt-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="px-2 md:px-3"
                  onClick={onAddExpense}
                >
                  <Plus className="h-4 w-4 md:mr-1.5" />
                  <span className="hidden md:inline">Nova</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="md:hidden">
                <p>Nova transação</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={onViewInvoice}
          >
            <Receipt className="h-4 w-4 mr-2" />
            Ver
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={onImport}
          >
            <FileUp className="h-4 w-4 mr-2" />
            Importar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
